/* eslint-disable react-refresh/only-export-components */
/**
 * DropdownMenu — Aurora-styled wrapper around Radix DropdownMenu.
 *
 * Use for the env switcher in the topbar, row actions in tables, and any
 * other contextual menu. Keyboard navigation and a11y come from Radix.
 */
import * as Radix from "@radix-ui/react-dropdown-menu";
import { Check } from "lucide-react";
import { forwardRef, type ComponentPropsWithoutRef } from "react";
import { cn } from "@/lib/cn";

export const DropdownMenu = Radix.Root;
export const DropdownMenuTrigger = Radix.Trigger;

export const DropdownMenuContent = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof Radix.Content>
>(({ className, sideOffset = 6, ...props }, ref) => (
  <Radix.Portal>
    <Radix.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        "z-50 min-w-[200px] overflow-hidden rounded-xl border border-line bg-surface p-1 text-fg",
        "shadow-[0_18px_50px_-22px_rgba(0,0,0,0.45)]",
        "data-[state=open]:animate-in data-[state=open]:fade-in data-[state=open]:zoom-in-95",
        className,
      )}
      {...props}
    />
  </Radix.Portal>
));
DropdownMenuContent.displayName = "DropdownMenuContent";

export const DropdownMenuItem = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof Radix.Item>
>(({ className, ...props }, ref) => (
  <Radix.Item
    ref={ref}
    className={cn(
      "relative flex cursor-pointer select-none items-center gap-2 rounded-md px-2.5 py-2 text-sm text-fg-dim outline-none",
      "data-[highlighted]:bg-aurora-soft data-[highlighted]:text-fg",
      "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className,
    )}
    {...props}
  />
));
DropdownMenuItem.displayName = "DropdownMenuItem";

export const DropdownMenuRadioGroup = Radix.RadioGroup;

export const DropdownMenuRadioItem = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof Radix.RadioItem>
>(({ className, children, ...props }, ref) => (
  <Radix.RadioItem
    ref={ref}
    className={cn(
      "relative flex cursor-pointer select-none items-center gap-2 rounded-md py-2 pl-8 pr-2.5 text-sm text-fg-dim outline-none",
      "data-[highlighted]:bg-aurora-soft data-[highlighted]:text-fg",
      "data-[state=checked]:text-fg",
      className,
    )}
    {...props}
  >
    <span className="absolute left-2 flex h-4 w-4 items-center justify-center">
      <Radix.ItemIndicator>
        <Check size={12} className="text-violet" />
      </Radix.ItemIndicator>
    </span>
    {children}
  </Radix.RadioItem>
));
DropdownMenuRadioItem.displayName = "DropdownMenuRadioItem";

export const DropdownMenuLabel = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof Radix.Label>
>(({ className, ...props }, ref) => (
  <Radix.Label
    ref={ref}
    className={cn("px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.12em] text-fg-mute", className)}
    {...props}
  />
));
DropdownMenuLabel.displayName = "DropdownMenuLabel";

export const DropdownMenuSeparator = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof Radix.Separator>
>(({ className, ...props }, ref) => (
  <Radix.Separator ref={ref} className={cn("my-1 h-px bg-line", className)} {...props} />
));
DropdownMenuSeparator.displayName = "DropdownMenuSeparator";
